import { Box } from '@mui/material';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { CampaignCreationSimulation } from 'modules/campaign-creation/components/CampaignCreationSimulation';
import { CampaignCreationSection } from 'pages/CampaignCreation/components/CampaignCreationSection';
import { OpeningMessage } from 'pages/CampaignCreation/components/OpeningMessage';
// TODO: Refactor this to use the new hooks
import { useQuery } from 'services/api/hooks';
import { getWhatsaapTemplates } from 'services/whatsaapTemplates/api';
import { WhatsaapTemplate } from 'services/whatsaapTemplates/types';
import { CampaignType, Channels } from 'generated-graphql/types';
import { ToastType, useToast } from 'services/toast';
import { useFormik } from 'formik';
import { ClosingMethod as ClosingMethodComponent } from 'pages/Campaigns/subpages/components/ClosingMethod';
import { FormValues } from 'pages/CampaignCreation/types';
// TODO: Fix this type
import { ClosingMethod } from 'services/campaign/types';
import { RadioGroup, TextField } from 'common/form';
import { NumberedSectionHeader } from 'common/ui';
import {
  OverlappingCampaignAction,
  OVERLAPPING_CAMPAIGNS_LABEL,
  overlappingCampaignsRadioOptions
} from 'modules/campaigns/components/SwitchCampaignOptionLabel';
import { CloseConversationTimeSelector } from 'pages/Campaigns/subpages/components/CloseConversationTimeSelector';
import { Tone } from 'generated-graphql/types';
// TODO: Remove this once refactoring old agent
import { Tone as OldTone } from 'services/agents/types';
import { CampaignCreationNextButton } from 'modules/campaign-creation/components/CampaignCreationNextButton';
import { CampaignLaunchButtons } from 'modules/campaign-creation/components/CampaignLaunchButtons';
import { getShouldShowAutomationStep } from 'modules/campaign-creation/utils/getShouldShowAutomationStep';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { getCampaignCreationSetupValidationSchema } from 'modules/campaign-creation/utils/getCampaignCreationSetupValidation';
import { useUser } from 'core/stores/hooks/useUser';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { Header } from 'common/ui/Header';
import { Paths } from 'services/navigation/paths';
import { getDefaultSubjectLine } from 'pages/CampaignCreation/utils/getDefaultSubjectLine';
import { getDefaultTemplatedMessage } from 'pages/CampaignCreation/utils/getDefaultTemplatedMessage';
import { useDefaultFieldsSetter } from 'pages/CampaignCreation/hooks/defaultFieldsSetter';
import { useSaveStateCampaign } from 'modules/campaign-creation/hooks/useSaveStateCampaign';
import { getTitle } from 'modules/campaign-creation/utils/getTitle';
import { getDefaultRejectionMessage } from 'modules/campaign-creation/utils/getDefaultRejectionMessage';
import { getDefaultClosingMessage } from 'modules/campaign-creation/utils/getDefaultClosingMessage';
import { useEffect, useMemo } from 'react';
import { getDefaultAgentName } from 'modules/campaign-creation/utils/getDefaultAgentName';
import { getDefaultCustomRequest } from 'modules/campaign-creation/utils/getDefaultCustomRequest';
import { handleAgentUpsert } from '../utils/handleAgentUpsert';
import { validateFormikForm } from 'modules/campaign-creation/utils/validateFormikForm';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import { CampaignLanguageSelector } from 'modules/campaign-creation/components/CampaignLanguageSelector';
import { toBaseLanguageCode } from 'utils/toBaseLanguageCode';

export type CampaignCreationDefaultValues =
  Partial<CampaignCreationSubmitFields>;

export type CampaignCreationSubmitFields = {
  agentId: string | undefined | null;
  agentName: string;
  agentTone: Tone;
  closingMethod: ClosingMethod;
  templateId: string | null;
  customTemplatedMessage: string | null;
  customTemplatedSubjectLine: string | null;
  calendarMeetingTemplateId: string | null;
  customRequest: string | null;
  rejectionMessage: string | null;
  closingMessage: string | null;
  timeToAutoCloseConversationsInHours: number | null;
  disableNudging: boolean;
  closePreviousConversations: boolean;
};

export const CampaignCreationSetupAndSimulationStep: React.FC = () => {
  const storeCampaignType = useCampaignCreationStore(
    (store) => store.state?.campaignType
  );
  const campaignType = storeCampaignType || CampaignType.EngagementOutreach;
  const channel = useCampaignCreationStore((store) => store.state?.channel);
  const language = useCampaignCreationStore((store) => store.state?.language);
  const setLanguage = useCampaignCreationStore((store) => store.setLanguage);
  const setupAndSimulationFields = useCampaignCreationStore(
    (store) => store.state?.setupAndSimulationFields
  );
  const onSetupAndSimulationSubmit = useCampaignCreationStore(
    (store) => store.onSetupAndSimulationSubmit
  );
  const onSetupAndSimulationBack = useCampaignCreationStore(
    (store) => store.onSetupAndSimulationBack
  );
  const state = useCampaignCreationStore((store) => store.state);
  const isCandidatePrioritisationEnabled = useFlag(
    Flags.CAMPAIGN_CANDIDATE_PRIORITISATION
  );

  // Check if automation step is needed to determine if this is the final step
  const shouldShowAutomationStep = getShouldShowAutomationStep({
    hasATSJobID: !!state?.ATSJobDescriptionState?.selectedJob,
    hasATSListID: !!state?.ATSImportState?.atsListId,
    questions: state?.questions || []
  });

  const organization = useOrganization();
  const user = useUser();
  const organizationConfig = useOrganizationConfig();
  const { showToast } = useToast();

  // Initialize language to the org's first available language when not set yet
  useEffect(() => {
    if (
      !language &&
      organizationConfig?.availableLanguages &&
      organizationConfig.availableLanguages.length > 1
    ) {
      setLanguage(organizationConfig.availableLanguages[0]);
    }
  }, [language, organizationConfig?.availableLanguages, setLanguage]);

  // Memoize initial values to prevent formik from thinking the form has changed
  const initialValues = useMemo(() => {
    return {
      templateId: setupAndSimulationFields?.templateId,
      customTemplatedMessage:
        setupAndSimulationFields?.customTemplatedMessage ||
        getDefaultTemplatedMessage({
          // TODO: Fix this typing
          campaignType: campaignType as any,
          channel: channel || Channels.Whatsapp
        }),
      customTemplatedMessageCharactersCount:
        setupAndSimulationFields?.customTemplatedMessage?.length || 0,
      customTemplatedSubjectLine:
        setupAndSimulationFields?.customTemplatedSubjectLine ||
        // TODO: Fix this typing
        getDefaultSubjectLine({ campaignType: campaignType as any }),
      closingMethod:
        setupAndSimulationFields?.closingMethod || ClosingMethod.CUSTOM,
      calendarMeetingTemplateId:
        setupAndSimulationFields?.calendarMeetingTemplateId || '',
      customRequest:
        setupAndSimulationFields?.customRequest ||
        organizationConfig?.customRequest ||
        getDefaultCustomRequest(organizationConfig),
      rejectionMessage:
        setupAndSimulationFields?.rejectionMessage ||
        getDefaultRejectionMessage({
          isEngagementOutreach:
            campaignType === CampaignType.EngagementOutreach,
          organizationConfig,
          savedRejectionMessage: organizationConfig?.rejectionMessage,
          userEmail: user?.email || undefined,
          websiteUrl: organization?.websiteUrl || undefined
        }),
      closingMessage:
        setupAndSimulationFields?.closingMessage ||
        getDefaultClosingMessage({
          organizationConfig,
          savedClosingMessage: organizationConfig?.closingMessage,
          userEmail: user?.email || undefined
        }),
      timeToAutoCloseConversationsInHours:
        setupAndSimulationFields?.timeToAutoCloseConversationsInHours || 168,
      agentName: getDefaultAgentName({
        organizationConfig,
        agentName: setupAndSimulationFields?.agentName,
        agentId: setupAndSimulationFields?.agentId,
        userName: user?.name || ''
      }),
      agentId: setupAndSimulationFields?.agentId || organizationConfig?.agentId,
      agentTone:
        setupAndSimulationFields?.agentTone ||
        organizationConfig?.agent?.tone ||
        Tone.Neutral,
      disableNudging: setupAndSimulationFields?.disableNudging ?? false,
      closePreviousConversations:
        setupAndSimulationFields?.closePreviousConversations ?? false
    };
  }, [
    setupAndSimulationFields,
    campaignType,
    channel,
    organizationConfig,
    user?.email,
    organization?.websiteUrl
  ]);

  // Memoize validation schema to prevent recreation on every render
  const validationSchema = useMemo(
    () =>
      getCampaignCreationSetupValidationSchema({
        campaignType,
        channel: channel || Channels.Whatsapp,
        openingMessageMode: organizationConfig?.openingMessageMode || undefined
      }),
    [campaignType, channel, organizationConfig?.openingMessageMode]
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      // TODO: Get type from the validation schema but it's not working
      const castValues = values as unknown as CampaignCreationSubmitFields;
      const closingMethod = castValues.closingMethod as ClosingMethod;
      const agentTone = castValues.agentTone;

      const agentId = await handleAgentUpsert({
        agentName: castValues.agentName,
        agentTone,
        organizationId: organization.id,
        userId: user?.id || ''
      });

      // NOTE: Being explict on submit here for type safety
      onSetupAndSimulationSubmit({
        templateId: castValues.templateId || null,
        customTemplatedMessage: castValues.customTemplatedMessage || null,
        customTemplatedSubjectLine:
          castValues.customTemplatedSubjectLine || null,
        closingMethod,
        calendarMeetingTemplateId: castValues.calendarMeetingTemplateId || '',
        customRequest: castValues.customRequest || null,
        rejectionMessage: castValues.rejectionMessage || null,
        closingMessage: castValues.closingMessage || null,
        timeToAutoCloseConversationsInHours:
          castValues.timeToAutoCloseConversationsInHours || null,
        agentName: castValues.agentName,
        agentId,
        agentTone,
        disableNudging: castValues.disableNudging ?? false,
        closePreviousConversations: castValues.closePreviousConversations
      });
    }
  });

  // Memoize save state parameters to prevent unnecessary saves
  const saveStateParams = useMemo(
    () => ({
      agentId: formik.values.agentId || undefined,
      agentName: formik.values.agentName || undefined,
      closingMethod: formik.values.closingMethod || undefined,
      templateId: formik.values.templateId || undefined,
      customTemplatedMessage: formik.values.customTemplatedMessage || undefined,
      customTemplatedSubjectLine:
        formik.values.customTemplatedSubjectLine || undefined,
      calendarMeetingTemplateId: formik.values.calendarMeetingTemplateId || '',
      customRequest: formik.values.customRequest || undefined,
      rejectionMessage: formik.values.rejectionMessage || undefined,
      closingMessage: formik.values.closingMessage || undefined,
      timeToAutoCloseConversationsInHours:
        formik.values.timeToAutoCloseConversationsInHours || undefined,
      disableNudging: formik.values.disableNudging,
      closePreviousConversations: formik.values.closePreviousConversations
    }),
    [
      formik.values.agentId,
      formik.values.agentName,
      formik.values.closingMethod,
      formik.values.templateId,
      formik.values.customTemplatedMessage,
      formik.values.customTemplatedSubjectLine,
      formik.values.calendarMeetingTemplateId,
      formik.values.customRequest,
      formik.values.rejectionMessage,
      formik.values.closingMessage,
      formik.values.timeToAutoCloseConversationsInHours,
      formik.values.disableNudging,
      formik.values.closePreviousConversations
    ]
  );

  useSaveStateCampaign(saveStateParams);

  // Memoize default fields setter parameters
  const defaultFieldsParams = useMemo(
    () => ({
      setFieldValue: formik.setFieldValue,
      // TODO: Fix this typing
      values: formik.values as unknown as FormValues,
      organizationConfig
    }),
    [formik.setFieldValue, formik.values, organizationConfig]
  );

  useDefaultFieldsSetter(defaultFieldsParams);

  // Update agentName when organizationConfig loads asynchronously.
  // Without this, Formik captures the userName fallback on first render
  // and never picks up the org config's agent name.
  useEffect(() => {
    if (
      organizationConfig?.agent?.name &&
      !setupAndSimulationFields?.agentName
    ) {
      formik.setFieldValue('agentName', organizationConfig.agent.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationConfig?.agent?.name]);

  const selectedLanguage =
    language || organizationConfig?.availableLanguages?.[0] || 'en_us';

  const { data: templatesList, isLoading: isWhatsAppTemplatesLoading } =
    useQuery<WhatsaapTemplate[]>(
      getWhatsaapTemplates,
      {
        organizationId: organization.id,
        filter: {
          campaignType: { eq: campaignType },
          channel: { eq: channel },
          ...((channel === Channels.Whatsapp || channel === Channels.Sms) && {
            language: { eq: toBaseLanguageCode(selectedLanguage) }
          })
        }
      },
      {
        onError: () => {
          showToast({
            message: 'Could not load templates.',
            type: ToastType.Error
          });
        }
      }
    );

  // Memoize template finding to prevent recalculation on every render
  const template = useMemo(
    () =>
      templatesList?.find(
        (template) => template.id === formik.values.templateId
      ),
    [templatesList, formik.values.templateId]
  );

  return (
    <>
      <Header
        title="Conversation settings"
        currentBreadcrumb={getTitle(state)}
        breadcrumbs={[
          {
            name: 'Campaigns',
            path: Paths.CAMPAIGNS
          }
        ]}
        onBack={() => {
          onSetupAndSimulationBack();
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '50px',
          mb: '50px'
        }}
      >
        {organizationConfig?.availableLanguages &&
          organizationConfig.availableLanguages.length > 1 && (
            <CampaignLanguageSelector
              availableLanguages={organizationConfig.availableLanguages}
              selectedLanguage={
                language ||
                organizationConfig?.availableLanguages?.[0] ||
                'en_us'
              }
              onChange={(lang) => {
                setLanguage(lang);
                formik.setFieldValue('templateId', '');
              }}
            />
          )}
        <CampaignCreationSection
          title="Opening message"
          subTitle="Choose a pre-approved message."
        >
          <OpeningMessage
            // TODO: Fix this typing
            campaignType={campaignType as any}
            isEdit={false}
            customTemplatedMessage={formik.values.customTemplatedMessage}
            customTemplatedSubjectLine={
              formik.values.customTemplatedSubjectLine
            }
            templateId={formik.values.templateId || ''}
            templatesList={templatesList || []}
            isWhatsAppTemplatesLoading={isWhatsAppTemplatesLoading}
            onSelectTemplate={(template) =>
              formik.setFieldValue('templateId', template.id)
            }
            // TODO: Fix this typing
            channel={channel as Channels}
            handleBlur={formik.handleBlur}
            setFieldValue={formik.setFieldValue}
            isError={(field) =>
              !!formik.errors[field as keyof typeof formik.errors]
            }
            errors={formik.errors}
            touched={formik.touched}
          />
        </CampaignCreationSection>
        <CampaignCreationSection
          title="Closing conversations"
          subTitle="Triggered depending on how a conversation closes:"
        >
          <>
            <NumberedSectionHeader
              number={1}
              title="Successful candidates"
              description="When all knockout questions (if added) are satisfied, and the candidate answers all questions. You can add links (URL) and phone numbers."
            >
              <ClosingMethodComponent
                isEdit={false}
                formValues={
                  {
                    customRequest: formik.values.customRequest,
                    calendarMeetingTemplateId:
                      formik.values.calendarMeetingTemplateId,
                    closingMethod: formik.values.closingMethod
                    // TODO: This is a hack cast but the child shouldn't be typed like that.
                  } as FormValues
                }
                errors={formik.errors}
                touched={formik.touched}
                handleBlur={formik.handleBlur}
                onChangeField={formik.setFieldValue}
                // TODO: This is a hack cast but the child shouldn't be typed like that.
                setFormValues={formik.setValues as any}
              />
            </NumberedSectionHeader>
            <NumberedSectionHeader
              number={2}
              title="Unsuccessful candidates"
              description="If the candidate does not satisfy a knockout question."
            >
              <TextField
                error={!!formik.errors.rejectionMessage}
                errorMessage={formik.errors.rejectionMessage}
                name="rejectionMessage"
                onChange={(value) =>
                  formik.setFieldValue('rejectionMessage', value)
                }
                value={formik.values.rejectionMessage || ''}
                field={{
                  type: 'textarea',
                  label: 'Write message',
                  rows: 5
                }}
              />
            </NumberedSectionHeader>
            <NumberedSectionHeader
              number={3}
              title="Candidates who are not interested"
              description="No further questions will be asked, and the conversation closes with the following message."
            >
              <TextField
                error={!!formik.errors.closingMessage}
                errorMessage={formik.errors.closingMessage}
                name="closingMessage"
                onChange={(value) =>
                  formik.setFieldValue('closingMessage', value)
                }
                value={formik.values.closingMessage || ''}
                field={{
                  type: 'textarea',
                  label: 'Write message',
                  rows: 5
                }}
              />
            </NumberedSectionHeader>
            <CloseConversationTimeSelector
              isEdit={false}
              timeToAutoCloseConversationsInHours={
                formik.values.timeToAutoCloseConversationsInHours || undefined
              }
              setTimeToAutoCloseConversationsInHours={(value) =>
                formik.setFieldValue(
                  'timeToAutoCloseConversationsInHours',
                  value
                )
              }
              error={
                formik.submitCount > 0
                  ? formik.errors.timeToAutoCloseConversationsInHours
                  : undefined
              }
            />
            <RadioGroup
              name="disableNudging"
              value={formik.values.disableNudging ? 'disabled' : 'enabled'}
              field={{
                label: 'Nudge non-responsive candidates',
                subLabel:
                  "Candidates who don't respond receive up to three reminders, 24 hours apart.",
                options: [
                  { label: 'Enabled', value: 'enabled' },
                  { label: 'Disabled', value: 'disabled' }
                ]
              }}
              onChange={(value) => {
                formik.setFieldValue('disableNudging', value === 'disabled');
              }}
            />
          </>
        </CampaignCreationSection>
        {isCandidatePrioritisationEnabled && (
          <CampaignCreationSection
            title="Overlapping campaigns"
            subTitle="When candidates are in other campaigns"
          >
            <RadioGroup
              name="closePreviousConversations"
              value={
                formik.values.closePreviousConversations
                  ? OverlappingCampaignAction.SWITCH
                  : OverlappingCampaignAction.WAIT
              }
              field={{
                containerSx: { maxWidth: 400 },
                label: OVERLAPPING_CAMPAIGNS_LABEL,
                options: overlappingCampaignsRadioOptions
              }}
              onChange={(value) => {
                formik.setFieldValue(
                  'closePreviousConversations',
                  value === OverlappingCampaignAction.SWITCH
                );
              }}
            />
          </CampaignCreationSection>
        )}
        {Object.keys(formik.errors).length === 0 && (
          <CampaignCreationSimulation
            template={template || null}
            customTemplatedMessage={
              formik.values.customTemplatedMessage || null
            }
            closingMethod={formik.values.closingMethod || ClosingMethod.CUSTOM}
            customRequest={formik.values.customRequest || null}
            rejectionMessage={formik.values.rejectionMessage || null}
            closingMessage={formik.values.closingMessage || null}
            agentName={formik.values.agentName || ''}
            agentTone={formik.values.agentTone as unknown as OldTone}
          />
        )}
        <Box sx={{ pl: '460px' }}>
          {shouldShowAutomationStep && (
            <CampaignCreationNextButton
              handleNext={() => {
                window.scrollTo({
                  top: 0,
                  behavior: 'auto'
                });
                formik.handleSubmit();
              }}
            />
          )}
          {!shouldShowAutomationStep && (
            <CampaignLaunchButtons
              validateAndGetTasks={() => validateFormikForm(formik)}
            />
          )}
        </Box>
      </Box>
    </>
  );
};
